import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { WaziWordmark } from "../components/Logo";
import {
  LayoutDashboard,
  Users,
  Inbox,
  FileText,
  Briefcase,
  Star,
  Send,
  LogOut,
  UserCircle2,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/workers", label: "Workers", icon: Users },
  { to: "/admin/leads", label: "Leads", icon: Inbox },
  { to: "/admin/quotes", label: "Quotes", icon: FileText },
  { to: "/admin/jobs", label: "Jobs", icon: Briefcase },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  { to: "/admin/broadcast", label: "Broadcast", icon: Send },
];

const AdminLayout = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("wazi_token");
    if (!token) { navigate("/admin/login"); return; }
    api.get("/auth/me").then((r) => setAdmin(r.data)).catch(() => {
      localStorage.removeItem("wazi_token");
      navigate("/admin/login");
    });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("wazi_token");
    localStorage.removeItem("wazi_admin");
    navigate("/admin/login");
  };

  if (!admin) return <div className="min-h-screen flex items-center justify-center bg-[#F7FAFC] text-[#718096]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#F7FAFC] flex">
      <aside className="hidden md:flex w-64 bg-white border-r border-[#E2E8F0] flex-col">
        <Link to="/" className="p-6 border-b border-[#E2E8F0]" data-testid="admin-logo">
          <WaziWordmark size={28} />
        </Link>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-[#25D366]/10 text-[#16A34A]" : "text-[#2D3748] hover:bg-[#F7FAFC]"
                }`
              }
            >
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#E2E8F0]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-[#1E88E5]/10 text-[#1E88E5] flex items-center justify-center">
              <UserCircle2 className="w-5 h-5" />
            </div>
            <div className="text-sm">
              <div className="font-heading font-semibold text-[#2D3748]">{admin.name}</div>
              <div className="text-xs text-[#718096] truncate max-w-[150px]">{admin.email}</div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="w-full flex items-center justify-center gap-2 text-sm text-[#DC2626] py-2 rounded-lg hover:bg-[#DC2626]/5"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <div className="bg-white border-b border-[#E2E8F0] px-4 md:px-8 py-4 flex items-center justify-between md:justify-end gap-3">
          <Link to="/" className="md:hidden" data-testid="admin-mobile-logo"><WaziWordmark size={26} /></Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-[#1E88E5] hover:underline" data-testid="admin-view-site">View site →</Link>
          </div>
        </div>
        <div className="p-4 md:p-8 max-w-7xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
